import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, ToggleButton, ToggleButtonGroup, Chip } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import * as API from '../../api';

const generateTextTemplate = (rooms) => {
    if (!rooms || rooms.length === 0) return '';
    const supplierName = rooms[0].supplier_name || 'Khách sạn/Resort';
    let text = `[Fola Travel] Đặt phòng - ${supplierName} - ${rooms.length} phòng\nFola Travel xin gửi yêu cầu đặt ${rooms.length} phòng như sau:\n==================================\n`;
    rooms.forEach((r, i) => {
        const cIn = r.check_in ? r.check_in.split('-').reverse().join('/') : '---';
        const cOut = r.check_out ? r.check_out.split('-').reverse().join('/') : '---';
        text += `${i + 1}. Khách: ${r.customer_detail?.name || 'Khách lẻ'} - SĐT: ${r.customer_detail?.phone || ''}\n   Mã BK: ${r.bk_code}\n   Hạng phòng: ${r.room_type} (SL: ${r.quantity})\n   Check-in: ${cIn} | Check-out: ${cOut} (${r.nights_count || 1} đêm)\n   Ghi chú: ${r.note || ''}\n----------------------------------\n`;
    });
    text += `\nĐại lý: Fola Travel - 0363463666 hoặc 0986332366 (Mr. Hùng).\nQuý công ty vui lòng xác nhận đặt chỗ tại đây.\nTrân trọng,`;
    return text;
};

const generateHtmlTemplate = (rooms) => {
    if (!rooms || rooms.length === 0) return '';
    const thStyle = `border: 1px solid #333; padding: 5px; background-color: #f2f2f2;`;
    const tdStyle = `border: 1px solid #333; padding: 5px; vertical-align: top;`;
    
    const rows = rooms.map(r => `
            <tr>
                <td style="${tdStyle}; text-align: center;">${r.bk_code}</td>
                <td style="${tdStyle}">${r.customer_detail?.name || 'Khách lẻ'} <br/> ${r.customer_detail?.phone || ''}</td>
                <td style="${tdStyle}">${r.room_type} <b>(SL: ${r.quantity})</b></td>
                <td style="${tdStyle}; text-align: center; font-weight: bold; color: #ed6c02;">IN: ${r.check_in ? r.check_in.split('-').reverse().join('/') : '---'} <br/> OUT: ${r.check_out ? r.check_out.split('-').reverse().join('/') : '---'}</td>
                <td style="${tdStyle}">${r.note || ''}</td>
            </tr>`).join('');

    return `<html>
<body style="font-family: Arial; line-height: 1.6;">
    <div>Fola Travel xin gửi yêu cầu đặt ${rooms.length} phòng:</div>
    
    <table style="width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 10px;">
        <thead>
            <tr>
                <th style="${thStyle}">Mã BK</th>
                <th style="${thStyle}">Khách hàng</th>
                <th style="${thStyle}">Hạng phòng</th>
                <th style="${thStyle}">Check-in / Check-out</th>
                <th style="${thStyle}">Ghi chú</th>
            </tr>
        </thead>
        <tbody>${rows}
        </tbody>
    </table>

    <div style="margin-top: 15px;"><strong>Đại lý: Fola Travel</strong></div>
    <div>--------------------------------------------------</div>
    <div style="margin-top: 10px;">Mọi thông tin chi tiết vui lòng liên hệ số hotline Fola Travel tại 0363463666 hoặc 0986332366 (Mr. Hùng).</div>
    <div>Quý công ty vui lòng xác nhận đặt chỗ qua email này.</div>
    <div style="margin-top: 10px;">Trân trọng,</div>
</body>
</html>`;
};

const HotelOrderDialog = ({ open, onClose, selectedRooms, showToast, onSuccess }) => {
    const [textContent, setTextContent] = useState('');
    const [htmlContent, setHtmlContent] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [ccEmail, setCcEmail] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [viewMode, setViewMode] = useState('EMAIL');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open && selectedRooms.length > 0) {
            setTextContent(generateTextTemplate(selectedRooms));
            setHtmlContent(generateHtmlTemplate(selectedRooms));
            const firstR = selectedRooms[0];
            setRecipientEmail(firstR.supplier_email || '');
            setCcEmail(firstR.supplier_cc_emails || '');
            setEmailSubject(`[Fola Travel] - Đặt phòng - Ngày ${firstR.check_in ? firstR.check_in.split('-').reverse().join('/') : ''} - ${selectedRooms.length} BK`);
            setViewMode('EMAIL');
        }
    }, [open, selectedRooms]);

    const handleSendEmail = async () => {
        setSending(true);
        try {
            await API.client.post('/booking-rooms/send_room_order_email/', { email: recipientEmail, cc: ccEmail, subject: emailSubject, content: htmlContent });
            await Promise.all(selectedRooms.map(r => API.client.patch(`/booking-rooms/${r.id}/update_status/`, { operation_status: 'CONTACTED' })));
            if (showToast) showToast('Đã gửi mail đặt phòng thành công!', 'success');
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) { if (showToast) showToast('Lỗi gửi mail.', 'error'); } finally { setSending(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}><Typography variant="h6">LỆNH ĐẶT PHÒNG KS ({selectedRooms.length} phòng)</Typography></DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'row', gap: 3, height: '520px' }}>
                
                {/* DANH SÁCH KHÁCH SẠN / PHÒNG BÊN TRÁI */}
                <Box sx={{ width: '340px', minWidth: '340px', borderRight: '1px solid #e0e0e0', pr: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2', textTransform: 'uppercase', pb: 0.5, borderBottom: '2px solid #1976d2' }}>
                        Danh sách phòng chọn
                    </Typography>
                    {selectedRooms.map((r, idx) => (
                        <Box key={r.id || idx} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f9f9f9', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Chip label={r.bk_code || 'N/A'} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold', height: '20px' }} />
                                <Typography variant="caption" color="textSecondary">Chặng {idx + 1}</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                                Khách: {r.customer_detail?.name || 'Khách lẻ'} {r.customer_detail?.phone ? `(${r.customer_detail.phone})` : ''}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#555' }}>
                                <b>Hạng phòng:</b> {r.room_type} <span style={{ color: '#1976d2', fontWeight: 'bold' }}>(SL: {r.quantity})</span>
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                                <b>Check-in:</b> {r.check_in ? r.check_in.split('-').reverse().join('/') : '---'}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                                <b>Check-out:</b> {r.check_out ? r.check_out.split('-').reverse().join('/') : '---'} ({r.nights_count || 1} đêm)
                            </Typography>
                            {r.note && (
                                <Typography variant="caption" display="block" sx={{ color: '#ed6c02', fontStyle: 'italic', mt: 0.5, bgcolor: '#fff3e0', p: 0.5, borderRadius: '4px' }}>
                                    <b>Ghi chú:</b> {r.note}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* NỘI DUNG SOẠN THẢO BÊN PHẢI */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField fullWidth size="small" label="Tiêu đề Email" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                    <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
                        <ToggleButton value="EMAIL"><EmailIcon sx={{ mr: 1, fontSize: '1rem' }} /> Email (HTML Preview)</ToggleButton>
                        <ToggleButton value="TEXT"><TextSnippetIcon sx={{ mr: 1, fontSize: '1rem' }} /> Bản Text (Copy Zalo)</ToggleButton>
                    </ToggleButtonGroup>
                    {viewMode === 'EMAIL' ? (
                        <TextField multiline fullWidth value={htmlContent} onChange={(e) => setHtmlContent(e.target.value)} sx={{ flex: 1, bgcolor: '#e3f2fd', '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', fontFamily: 'monospace', fontSize: '0.85rem' } }} />
                    ) : (
                        <TextField multiline fullWidth value={textContent} onChange={(e) => setTextContent(e.target.value)} sx={{ flex: 1, bgcolor: '#f9fbe7', '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start', fontFamily: 'monospace', fontSize: '0.9rem' } }} />
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                <Box sx={{ flex: 1, display: 'flex', gap: 1 }}>{viewMode === 'EMAIL' && <><TextField fullWidth size="small" label="Gửi tới (Email Khách sạn)" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} /><TextField fullWidth size="small" label="CC Email" value={ccEmail} onChange={(e) => setCcEmail(e.target.value)} /></>}</Box>
                <Button onClick={onClose} color="inherit">Đóng</Button>
                {viewMode === 'TEXT' && <Button variant="contained" color="success" onClick={() => { navigator.clipboard.writeText(textContent); if (showToast) showToast('Đã copy nội dung bản text thành công!', 'success'); }}>Copy Bản Text</Button>}
                {viewMode === 'EMAIL' && <Button variant="contained" onClick={handleSendEmail} disabled={sending || !recipientEmail}>{sending ? 'Đang gửi...' : 'Gửi Email đặt phòng'}</Button>}
            </DialogActions>
        </Dialog>
    );
};
export default HotelOrderDialog;